/**
 * merge.ts — `{{merge.field}}` resolution and value sanitisation.
 *
 * VENDORED, DELIBERATELY VERBATIM, from the edge renderer at
 * `fleet-backend/supabase/functions/generate-agreement/render/merge.ts`. The on-screen
 * review in this app and the archived PDF must agree about what a document says, and the
 * only way to guarantee that is for both to run the same substitution, the same
 * sanitisation and the same marks. Keep the two files byte-identical below the header —
 * the behavioural invariants are pinned by `__tests__/agreementRender.test.ts`, which is
 * what fails first if one side drifts.
 *
 * A merge value is UNTRUSTED TEXT that lands inside a legal document. Two things
 * therefore happen to every value before it reaches the markdown parser:
 *
 *   1. It is FLATTENED — control characters removed, every whitespace run collapsed
 *      to a single space — so a value can never introduce a line, and therefore can
 *      never open a `:::` container or a heading of its own.
 *   2. It is ESCAPED — every ASCII punctuation character markdown gives meaning to is
 *      backslash-escaped, so a driver whose address contains `**` or `|` cannot bold a
 *      clause, split a table row, or inject a rule. markdown-it unescapes them again
 *      at inline-parse time, so the printed glyphs are exactly what was stored.
 *
 * Resolved and unresolved values are then wrapped in control-character marks that the
 * markdown renderer turns into per-run spans. The marks are U+001F / U+001D / U+001E:
 * they cannot survive sanitisation (step 1 strips them from values) and they cannot
 * appear in a template body (step 0 strips them from the source), so a mark in the
 * stream is always ours.
 *
 * NUMBERS ARE NOT FORMATTED HERE. `agreement_merge_data` supplies display-ready
 * strings — money is formatted once, in SQL, where the rounding rule lives. A number
 * that arrives anyway is stringified verbatim rather than being locale-formatted,
 * because a renderer that quietly re-rounds a figure is a financial defect.
 *
 * One namespace is resolved AFTER the content hash rather than before it — see
 * `SIGNATURE_NAMESPACE` at the foot of this file.
 */

/** Opens a run carrying a resolved merge value. */
export const MARK_VALUE = '\u001F';
/** Opens a run carrying an unresolved (missing) merge field. */
export const MARK_MISSING = '\u001D';
/** Closes either. */
export const MARK_END = '\u001E';

const MARK_CHARS = new RegExp(`[${MARK_VALUE}${MARK_MISSING}${MARK_END}]`, 'g');

/**
 * `{{driver.full_name}}` — a dotted path between the braces and NOTHING ELSE.
 *
 * NO INNER WHITESPACE, deliberately, because SQL has none to give. `agreement_render` merges
 * with `replace(body, '{{' || key || '}}', value)` — a literal string swap, not a pattern —
 * so `{{ driver.full_name }}` is text SQL never touches. A regex here that accepted the
 * spaced form would resolve on screen what the database left literal, and the two would
 * disagree about what the document says: the review pane would show a name where the hashed
 * text, the archived PDF and the driver's copy all show braces.
 *
 * EVERY SEGMENT STARTS WITH A LETTER OR AN UNDERSCORE, the one after a dot included, which is
 * the grammar `agreement_template_publish` refuses a body against character for character. A
 * looser `[A-Za-z0-9_]+` for the trailing segments would resolve `{{a.1b}}` on screen and see
 * the same template refused at publish.
 *
 * The spaced form is therefore not a tolerated spelling but a template DEFECT, and it is
 * caught where it can still be fixed — `malformedMergeFields` in `templateCatalog.ts` flags it
 * in the editor while the line is being written, and `agreement_template_publish` refuses to
 * SEAL it with `AGREEMENT_TEMPLATE_MALFORMED_FIELD`. `agreement_template_update_draft` saves
 * it without complaint, deliberately: a draft is a work in progress, and the save button is not
 * the place to argue with half a sentence.
 */
const MERGE_PATTERN = /\{\{([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\}\}/g;

/** Printed in place of a field the merge data does not carry. */
export const MISSING_PLACEHOLDER = '____________';

/** Hard ceiling on one merge value, so a runaway free-text column cannot swallow a page. */
export const MAX_VALUE_LENGTH = 512;

/** ASCII punctuation markdown gives meaning to, plus `:` (container fences) and `<`/`>`. */
const MARKDOWN_ACTIVE = /[\\`*_{}[\]()#+\-.!|<>~:]/g;

/** C0 and C1 control characters, including our own marks. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g;

export interface MergeOptions {
    /** Replaces an unresolved field with this text. Defaults to `MISSING_PLACEHOLDER`. */
    missingPlaceholder?: string;
    /** Per-value ceiling. Defaults to `MAX_VALUE_LENGTH`. */
    maxValueLength?: number;
    /**
     * Restricts substitution to fields under this dotted prefix. Every other `{{field}}` is
     * left EXACTLY as it was found — not resolved, not marked, not counted as missing.
     *
     * This exists for one caller: the presentation pass over markdown SQL has already
     * merged. `agreement_render` substitutes every value it can and leaves the rest
     * literal, and that text is what `content_sha256` covers. Resolving only the
     * `signature.` namespace over it means the page can differ from the hashed text in
     * that namespace and nowhere else.
     */
    namespace?: string;
}

export interface MergeOutcome {
    /** The template body with every `{{field}}` replaced and marked. */
    text: string;
    /** Every field the body asked for that the data could not answer, in first-use order. */
    missing: string[];
    /** Every field the body asked for that resolved, in first-use order. */
    resolved: string[];
}

/** Strips our marks out of a template body, so only the merge step can produce them. */
export function stripMarks(source: string): string {
    return source.replace(MARK_CHARS, '');
}

/**
 * Flattens and escapes one value. Returns "" for null/undefined/blank, which the caller
 * reads as "missing".
 */
export function sanitizeMergeValue(value: unknown, maxLength = MAX_VALUE_LENGTH): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';

    let text: string;
    if (typeof value === 'number') {
        // Never re-round, never locale-format: SQL owns money presentation.
        text = Number.isFinite(value) ? String(value) : '';
    } else if (typeof value === 'string') {
        text = value;
    } else {
        // Objects and arrays are a merge-data defect, not something to print structurally.
        text = '';
    }

    text = text.replace(CONTROL_CHARS, ' ').replace(/\s+/g, ' ').trim();
    if (text.length === 0) return '';
    if (text.length > maxLength) text = `${text.slice(0, maxLength - 1).trimEnd()}…`;
    return text.replace(MARKDOWN_ACTIVE, (ch) => `\\${ch}`);
}

/**
 * Resolves a dotted path against merge data in either shape.
 *
 * `agreement_merge_data` emits a FLAT map keyed by the dotted path itself
 * (`{"driver.full_name": "Ama Boateng"}`), because SQL replaces `{{key}}` by direct
 * substitution. A nested object is accepted too, so the same function serves data
 * assembled in TypeScript. The flat key wins when both could match: it is what the
 * database sends, and it is unambiguous.
 */
export function lookupMergePath(data: unknown, path: string): unknown {
    if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
        const flat = (data as Record<string, unknown>)[path];
        if (flat !== undefined) return flat;
    }

    let cursor: unknown = data;
    for (const segment of path.split('.')) {
        if (cursor === null || cursor === undefined) return undefined;
        if (typeof cursor !== 'object' || Array.isArray(cursor)) return undefined;
        cursor = (cursor as Record<string, unknown>)[segment];
    }
    return cursor;
}

/**
 * Replaces every `{{field}}` in `body` from `data` — or, with `options.namespace`, only
 * the fields under that prefix, leaving the rest of the body byte-for-byte as it arrived.
 *
 * Every replacement — resolved or not — is wrapped in marks, so the renderer can tint
 * merge fields on request without a second pass over the text. `missing` is the
 * pre-flight list the board shows before a document may be sent for signature.
 *
 * ONE PASS ONLY. `stripMarks` runs first, so feeding this function its own output erases
 * the marks the earlier pass produced. Merge everything from a single map instead.
 */
export function applyMergeFields(
    body: string,
    data: unknown,
    options: MergeOptions = {},
): MergeOutcome {
    const placeholder = options.missingPlaceholder ?? MISSING_PLACEHOLDER;
    const maxLength = options.maxValueLength ?? MAX_VALUE_LENGTH;
    const namespace = options.namespace;
    const missing: string[] = [];
    const resolved: string[] = [];

    const text = stripMarks(body).replace(MERGE_PATTERN, (match: string, path: string) => {
        if (namespace !== undefined && !path.startsWith(namespace)) return match;
        const value = sanitizeMergeValue(lookupMergePath(data, path), maxLength);
        if (value.length === 0) {
            if (!missing.includes(path)) missing.push(path);
            return `${MARK_MISSING}${placeholder}${MARK_END}`;
        }
        if (!resolved.includes(path)) resolved.push(path);
        return `${MARK_VALUE}${value}${MARK_END}`;
    });

    return { text, missing, resolved };
}

// ─── The signature presentation namespace ────────────────────────────────────

/**
 * `{{signature.<party>.<suffix>}}` — the ONE namespace that is resolved after hashing.
 *
 * A document freezes at the FIRST signature, and `frozen_rendered_md` is write-once, so
 * every later party's signature block is still a literal placeholder inside the text every
 * party attested to. That is correct: a countersignature cannot be part of the bytes the
 * driver signed. The values are therefore resolved at PRESENTATION time — here — and
 * `content_sha256` keeps meaning "the text every party attested to", unchanged by anyone
 * signing after the freeze.
 *
 * Use with `applyMergeFields(rendered, values, { namespace: SIGNATURE_NAMESPACE })`.
 */
export const SIGNATURE_NAMESPACE = 'signature.';

/** The suffixes `agreement_merge_data` emits per SIGNED party. */
export const SIGNATURE_SUFFIXES = ['name', 'date', 'phone', 'address', 'title'] as const;

/** Only the `party` is needed; a signature row or a `parties[]` entry both satisfy it. */
export interface SignatureParty {
    party: string;
}

/**
 * Builds the presentation map for one document's signature blocks.
 *
 * `liveValues` is `agreement_render`'s CURRENT merge map — not the frozen snapshot, which
 * only ever knew about the first signer. SQL emits a `signature.*` key for a party only
 * once that party's row is SIGNED, and it formats the date and the company title there, so
 * the values are taken verbatim rather than re-derived: a renderer that formats a date a
 * second way is a renderer that eventually disagrees with the record.
 *
 * Every party on the document is seeded with a blank first, so a party who has NOT signed
 * resolves to the awaiting-signature fill-in rule instead of printing raw braces on the
 * page. A party absent from both inputs still cannot print braces — an unmatched field in
 * the namespace degrades to the same fill-in.
 */
export function signaturePresentationValues(
    parties: readonly SignatureParty[],
    liveValues: Record<string, unknown> | null | undefined,
): Record<string, string> {
    const values: Record<string, string> = {};

    for (const { party } of parties) {
        const key = String(party ?? '').toLowerCase();
        if (key.length === 0) continue;
        for (const suffix of SIGNATURE_SUFFIXES) {
            values[`${SIGNATURE_NAMESPACE}${key}.${suffix}`] = '';
        }
    }

    for (const [key, value] of Object.entries(liveValues ?? {})) {
        if (!key.startsWith(SIGNATURE_NAMESPACE)) continue;
        if (typeof value === 'string' && value.length > 0) values[key] = value;
    }

    return values;
}

/** Every `{{field}}` a body references, deduplicated, in first-use order. */
export function collectMergeFields(body: string): string[] {
    const fields: string[] = [];
    for (const match of body.matchAll(MERGE_PATTERN)) {
        const path = match[1];
        if (!fields.includes(path)) fields.push(path);
    }
    return fields;
}
